import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import KeyIcon from '@mui/icons-material/VpnKey'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  useCompanies,
  useCompanyOutlets,
  useMoveUserCompany,
  useSetRoleEnabled,
  useResetUserPassword,
  useRemoveRole,
  useRoles,
  useSetUserEnabled,
  useUsers,
} from '@/api/hooks'
import type { AppUser, RoleView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { RoleDialog } from '@/pages/access/RoleDialog'
import { UserDialog } from '@/pages/access/UserDialog'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatMobile,
  formatMoney,
  humanise,
  PageHeader,
} from '@/components/common'

const TAB_KEYS = ['users', 'roles'] as const

/** Who may sign in, what they may do, and where they may do it. */
export function AccessPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const initial = Math.max(TAB_KEYS.indexOf((requested ?? 'users') as (typeof TAB_KEYS)[number]), 0)
  const [tab, setTab] = useState(initial)

  return (
    <>
      <PageHeader
        title="Users and access"
        subtitle="Who may sign in, what they may do, and which outlets they may do it in"
      />
      <Card>
        <Tabs
          value={tab}
          onChange={(_event, value) => {
            setTab(value)
            setSearchParams({ tab: TAB_KEYS[value] })
          }}
        >
          <Tab label="Users" />
          <Tab label="Roles" />
        </Tabs>
        <Divider />
        <CardContent>
          {tab === 0 && <UsersTab />}
          {tab === 1 && <RolesTab />}
        </CardContent>
      </Card>
    </>
  )
}

function UsersTab() {
  const navigate = useNavigate()
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const signedInAs = useAuthStore((state) => state.user)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  // Whose people are being looked at. A platform administrator can look at
  // another company's, which is what lets somebody set up there be found again.
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const { data: users, error } = useUsers(companyId)
  const { data: roles } = useRoles()
  const { data: companies } = useCompanies()
  // Every outlet of the company being looked at, switched-off ones included, so
  // a person mapped to one reads as mapped to it rather than to a bare id.
  const { data: outlets } = useCompanyOutlets(companyId ?? ownCompanyId)
  const setEnabled = useSetUserEnabled()
  const resetPassword = useResetUserPassword()
  const moveCompany = useMoveUserCompany()
  const [editing, setEditing] = useState<AppUser | null>(null)
  const [creating, setCreating] = useState(false)
  const [resetting, setResetting] = useState<AppUser | null>(null)
  const [disabling, setDisabling] = useState<AppUser | null>(null)
  const [moving, setMoving] = useState<AppUser | null>(null)
  const [moveTo, setMoveTo] = useState('')
  const [moveReason, setMoveReason] = useState('')
  const [newPassword, setNewPassword] = useState('')

  const writable = canWrite('user')
  const roleName = new Map((roles ?? []).map((role) => [role.code, role.name]))
  const outletName = new Map((outlets ?? []).map((outlet) => [outlet.id, outlet.name]))
  const companyName = new Map((companies ?? []).map((company) => [company.id, company.name]))
  const canPickCompany = can('platform.admin') && (companies ?? []).length > 1

  return (
    <>
      <ErrorBanner error={error ?? setEnabled.error ?? resetPassword.error ?? moveCompany.error} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        {canPickCompany ? (
          <TextField
            select
            size="small"
            label="Company"
            value={companyId ?? ownCompanyId ?? ''}
            onChange={(event) => setCompanyId(event.target.value)}
            sx={{ minWidth: 240 }}
            inputProps={{ 'aria-label': 'Company' }}
          >
            {(companies ?? []).map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <span />
        )}
        {writable ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New user
          </Button>
        ) : null}
      </Stack>

      {!users || users.length === 0 ? (
        <EmptyState message="No users yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              {canPickCompany ? <TableCell>Company</TableCell> : null}
              <TableCell>Roles</TableCell>
              <TableCell>Outlets</TableCell>
              <TableCell>Last signed in</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover sx={{ opacity: user.active ? 1 : 0.6 }}>
                <TableCell>
                  <Typography variant="body2">{user.fullName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.username}
                    {user.mobile ? ` · ${formatMobile(user.mobile)}` : ''}
                  </Typography>
                </TableCell>
                {canPickCompany ? (
                  <TableCell>
                    <Typography variant="caption">
                      {companyName.get(user.companyId) ?? '—'}
                    </Typography>
                  </TableCell>
                ) : null}
                <TableCell>
                  {user.roleCodes.length === 0 ? (
                    <Typography variant="caption" color="warning.main">
                      No role
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {user.roleCodes.map((code) => (
                        <Chip key={code} size="small" variant="outlined" label={roleName.get(code) ?? code} />
                      ))}
                    </Stack>
                  )}
                </TableCell>
                <TableCell>
                  {user.outletIds.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      Every outlet
                    </Typography>
                  ) : (
                    <Typography variant="caption">
                      {user.outletIds.map((id) => outletName.get(id) ?? id).join(', ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{formatDateTime(user.lastLoginAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={user.locked ? 'error' : user.active ? 'success' : 'default'}
                    label={user.locked ? 'Locked' : user.active ? 'Active' : 'Disabled'}
                  />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {writable ? (
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          aria-label={`Edit ${user.fullName}`}
                          onClick={() => setEditing(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View this person's transaction log">
                        <IconButton
                          size="small"
                          aria-label={`View transaction log for ${user.fullName}`}
                          onClick={() =>
                            navigate(
                              `/audit-log?performedBy=${user.id}&name=${encodeURIComponent(user.fullName)}`,
                            )
                          }
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Somebody set up in the wrong business, or who has
                          changed which of them they work for. Offered only
                          where there is another business to move them to. */}
                      {canPickCompany ? (
                        <Tooltip title="Move to another company">
                          <IconButton
                            size="small"
                            aria-label={`Move ${user.fullName} to another company`}
                            onClick={() => {
                              setMoving(user)
                              setMoveTo('')
                              setMoveReason('')
                            }}
                          >
                            <SwapHorizIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Set a new password">
                        <IconButton
                          size="small"
                          aria-label={`Reset password for ${user.fullName}`}
                          onClick={() => {
                            setResetting(user)
                            setNewPassword('')
                          }}
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Signing yourself out of your own account is a trap, and
                          switching someone else off is not something to do by
                          brushing a button - it takes their login away. */}
                      <Button
                        size="small"
                        color={user.active ? 'error' : 'primary'}
                        disabled={user.id === signedInAs?.id || setEnabled.isPending}
                        onClick={() => (user.active ? setDisabling(user) : setEnabled.mutate({
                          id: user.id,
                          enabled: true,
                        }))}
                      >
                        {user.active ? 'Disable' : 'Enable'}
                      </Button>
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {resetting ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <TextField
            size="small"
            type="password"
            label={`New password for ${resetting.fullName}`}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            helperText="At least 10 characters"
            sx={{ minWidth: 320 }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={newPassword.length < 10 || resetPassword.isPending}
            onClick={() =>
              resetPassword.mutate(
                { id: resetting.id, newPassword },
                {
                  onSuccess: () => {
                    setResetting(null)
                    setNewPassword('')
                  },
                },
              )
            }
          >
            Set it
          </Button>
          <Button size="small" onClick={() => setResetting(null)}>
            Cancel
          </Button>
        </Stack>
      ) : null}

      <Dialog open={Boolean(moving)} onClose={() => setMoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Move {moving?.fullName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              They keep their username, their password and their sign-in history. What they did in{' '}
              {companyName.get(moving?.companyId ?? '') ?? 'their present company'} stays there.
            </Typography>
            <TextField
              select
              fullWidth
              label="Move them to"
              value={moveTo}
              onChange={(event) => setMoveTo(event.target.value)}
            >
              {(companies ?? [])
                .filter((company) => company.id !== moving?.companyId)
                .map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              fullWidth
              label="Why (optional)"
              value={moveReason}
              onChange={(event) => setMoveReason(event.target.value)}
              helperText="Kept with the audit entry"
            />
            {/* Said before it happens rather than discovered afterwards: both
                belong to the company they are leaving. */}
            <Alert severity="warning">
              The outlets they are mapped to, and any role that company wrote for itself, are
              dropped. Built-in roles move with them. Give them the new company's roles and outlets
              afterwards.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoving(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!moveTo || moveCompany.isPending}
            onClick={() =>
              moveCompany.mutate(
                { id: moving!.id, companyId: moveTo, reason: moveReason || undefined },
                {
                  onSuccess: (moved) => {
                    setMoving(null)
                    // Follow them, rather than leaving the list showing the
                    // company they are no longer in and no sign of where they
                    // went.
                    setCompanyId(moved.companyId)
                  },
                },
              )
            }
          >
            Move them
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(disabling)} onClose={() => setDisabling(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Switch off {disabling?.fullName}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            They will not be able to sign in until someone turns the account back on. Anything they
            raised stays exactly as it is.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisabling(null)}>Keep it on</Button>
          <Button
            color="error"
            variant="contained"
            disabled={setEnabled.isPending}
            onClick={() =>
              setEnabled.mutate(
                { id: disabling!.id, enabled: false },
                { onSuccess: () => setDisabling(null) },
              )
            }
          >
            Switch it off
          </Button>
        </DialogActions>
      </Dialog>

      <UserDialog open={creating} onClose={() => setCreating(false)} />
      <UserDialog open={Boolean(editing)} user={editing} onClose={() => setEditing(null)} />
    </>
  )
}

function RolesTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data: roles, error } = useRoles()
  const removeRole = useRemoveRole()
  const setRoleEnabled = useSetRoleEnabled()
  const [editing, setEditing] = useState<RoleView | null>(null)
  const [creating, setCreating] = useState(false)

  const writable = canWrite('user')

  return (
    <>
      <ErrorBanner error={error ?? setRoleEnabled.error} />
      {writable ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New role
          </Button>
        </Stack>
      ) : null}

      {!roles || roles.length === 0 ? (
        <EmptyState message="No roles configured." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell align="right">Permissions</TableCell>
              <TableCell align="right">Users</TableCell>
              <TableCell align="right">Approval limit</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id} hover sx={{ opacity: role.active ? 1 : 0.55 }}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{role.name}</Typography>
                    {role.systemRole ? <Chip size="small" variant="outlined" label="Built in" /> : null}
                    {role.active ? null : <Chip size="small" label="Withdrawn" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {role.code}
                    {role.description ? ` · ${role.description}` : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">{role.permissions.length}</TableCell>
                <TableCell align="right">{role.userCount}</TableCell>
                <TableCell align="right">
                  {role.approvalLimit == null ? '—' : formatMoney(role.approvalLimit)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={role.systemRole ? 'See what it may do' : 'Edit'}>
                    <IconButton
                      size="small"
                      aria-label={`Open ${role.name}`}
                      onClick={() => setEditing(role)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {writable && !role.systemRole ? (
                    <Button
                      size="small"
                      color={role.active ? 'error' : 'primary'}
                      disabled={setRoleEnabled.isPending}
                      onClick={() =>
                        setRoleEnabled.mutate({ id: role.id, enabled: !role.active })
                      }
                    >
                      {role.active ? 'Withdraw' : 'Restore'}
                    </Button>
                  ) : null}
                  {/* Withdrawing keeps a role that people hold; deleting is for
                      one created by mistake, so it is offered only where
                      nobody holds it and the server refuses otherwise. */}
                  {writable && !role.systemRole && role.userCount === 0 ? (
                    <Tooltip title="Delete this role">
                      <IconButton
                        size="small"
                        aria-label={`Delete ${role.name}`}
                        disabled={removeRole.isPending}
                        onClick={() => removeRole.mutate(role.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Changing what a role may do takes effect at once for everyone holding it — nobody has to sign
        in again. {humanise('BUILT_IN')} roles are read only.
      </Typography>

      <RoleDialog open={creating} onClose={() => setCreating(false)} />
      <RoleDialog open={Boolean(editing)} role={editing} onClose={() => setEditing(null)} />
    </>
  )
}
